// Per-signature pending tracker. The middleware that wraps the CommandBus
// adds + removes signatures here as dispatches start + finish. Views subscribe
// per signature via React's useSyncExternalStore to render disabled + spinner
// state without re-reading the global store on every render.
//
// Why a custom store and not just useState? Because the SAME signature can be
// observed by many components at once (a button in a list cell, a row's
// trailing actions, a kanban card on the same record) — those should all
// re-render together when the signature flips. A plain useState in one
// component can't fan out across the tree.

export type Listener = () => void;

export interface PendingCommandRegistry {
    has(signature: string): boolean;
    add(signature: string): void;
    remove(signature: string): void;
    // Per-signature subscription — listener fires whenever `has(signature)`
    // would flip. Returns unsubscribe.
    subscribe(signature: string, listener: Listener): () => void;
    // Snapshot for tests / devtools.
    snapshot(): ReadonlySet<string>;
}

export function createPendingCommandRegistry(): PendingCommandRegistry {
    const pending = new Set<string>();
    const listeners = new Map<string, Set<Listener>>();

    const notify = (signature: string): void => {
        const set = listeners.get(signature);
        if (!set) return;
        // Snapshot listeners before iterating in case a listener removes itself.
        for (const fn of Array.from(set)) fn();
    };

    return {
        has: (signature) => pending.has(signature),
        add(signature) {
            if (pending.has(signature)) return;
            pending.add(signature);
            notify(signature);
        },
        remove(signature) {
            if (!pending.has(signature)) return;
            pending.delete(signature);
            notify(signature);
        },
        subscribe(signature, listener) {
            let set = listeners.get(signature);
            if (!set) {
                set = new Set();
                listeners.set(signature, set);
            }
            set.add(listener);
            return () => {
                const s = listeners.get(signature);
                if (!s) return;
                s.delete(listener);
                if (s.size === 0) listeners.delete(signature);
            };
        },
        snapshot: () => pending,
    };
}
